import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'
import { JobSkillsManagerBody } from '@/components/employer/job-skills-manager-body'
import type { JobSkillDraft } from '@/components/employer/job-skill-manager-row'
import {
  useUpdateSimulationJob,
  type SimulationOpportunitySkill,
  type SimulationScenario,
} from '@/hooks/use-platform-simulation'
import { serializeJobSkillsForCompare } from '@/lib/job-skill-suggestions'

type DataBenchJobSkillsCanvasProps = {
  scenarioId: string
  organisationSlug: string
  skills: SimulationOpportunitySkill[]
  onClose: () => void
  onSaved?: (updated: SimulationScenario) => void
}

/** Sample job skills — saves through Simulation, not employer publish. */
export function DataBenchJobSkillsCanvas({
  scenarioId,
  organisationSlug,
  skills: initialSkills,
  onClose,
  onSaved,
}: DataBenchJobSkillsCanvasProps) {
  const updateJob = useUpdateSimulationJob()
  const didHydrateRef = useRef(false)
  const [skills, setSkills] = useState<JobSkillDraft[]>([])

  useLayoutEffect(() => {
    didHydrateRef.current = false
  }, [scenarioId])

  useLayoutEffect(() => {
    if (didHydrateRef.current) return
    didHydrateRef.current = true
    setSkills(
      initialSkills.map((row) => ({
        skill_id: row.skill_id,
        skill_name: row.skill_name,
        requirement_type: row.requirement_type === 'preferred' ? 'preferred' : 'required',
      })),
    )
  }, [initialSkills, scenarioId])

  const baselineSerialized = useMemo(
    () =>
      serializeJobSkillsForCompare(
        initialSkills.map((row) => ({
          skill_id: row.skill_id,
          skill_name: row.skill_name,
          requirement_type: row.requirement_type,
        })),
      ),
    [initialSkills],
  )
  const draftSerialized = useMemo(() => serializeJobSkillsForCompare(skills), [skills])
  const isDirty = baselineSerialized !== draftSerialized

  const serverError =
    updateJob.error instanceof Error ? updateJob.error.message : updateJob.error ? String(updateJob.error) : null

  const handleSave = () => {
    const payload: SimulationOpportunitySkill[] = skills.map((row) => ({
      skill_id: row.skill_id,
      skill_name: row.skill_name,
      requirement_type: row.requirement_type,
    }))
    void updateJob
      .mutateAsync({
        scenarioId,
        skills: payload,
      })
      .then((updated) => {
        onSaved?.(updated)
        onClose()
      })
      .catch(() => {
        // server error surfaces via updateJob.error
      })
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <JobSkillsManagerBody
          skills={skills}
          onSkillsChange={setSkills}
          organisationSlug={organisationSlug}
          disabled={updateJob.isPending}
        />
      </div>

      <ArtifactCanvasInlineError>{serverError}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={updateJob.isPending}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
